import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  BoltIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ClockIcon,
  DocumentTextIcon,
  StopCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import ConfirmDialog from '../MasterAdmin/ui/ConfirmDialog';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { inputClass } from './whatsappCampaignFormUtils';
import { StatCard, StatusBadge, formatDateTime, toDatetimeLocalValue, EmptyState } from './whatsappCampaignUi';
import {
  fetchCampaignMetaLimitRetries,
  fetchCampaignRcsRetries,
  fetchRcsStatus,
  type MetaLimitedCohortAnalysis,
  type MetaLimitAttemptAnalysis,
} from '../../services/MasterAdmin/whatsappCampaignService';

export type RetargetScheduleSummary = {
  hasQueue: boolean;
  pending: number;
  isScheduled: boolean;
  nextSendAt?: string | null;
  scheduleMode?: string | null;
};

export type RetargetQueueItem = {
  id: string;
  phone: string;
  status: string;
  attemptCount?: number;
  maxAttempts?: number;
  sendAt?: string | null;
  lastError?: string | null;
  lastErrorCode?: string | null;
  rcsTemplateName?: string;
  meta?: {
    attemptsLog?: Array<{ attempt: number; at: string; sent: boolean; reason?: string | null }>;
    scheduleMode?: string;
    scheduledAt?: string;
  };
};

export type RetargetDeliveryLog = {
  id: string;
  phone: string;
  status: string;
  rcsTemplateName?: string;
  interaktMessageId?: string | null;
  errorMessage?: string | null;
  sentAt?: string | null;
  createdAt?: string | null;
};

type Props = {
  variant: 'meta' | 'rcs';
  campaignId: string;
  eligibleCount: number;
  stats: Record<string, number> | null;
  schedule: RetargetScheduleSummary | null;
  items: RetargetQueueItem[];
  logs?: RetargetDeliveryLog[];
  analysis?: MetaLimitedCohortAnalysis | null;
  attemptAnalysis?: MetaLimitAttemptAnalysis | null;
  loading?: boolean;
  /** When true, show an empty-state card instead of rendering nothing */
  showWhenEmpty?: boolean;
  onRefresh: () => Promise<void>;
  onEnqueue: (mode: 'immediate' | 'schedule' | 'policy24h', sendAt?: string, options?: { rcsTemplateName?: string }) => Promise<void>;
  onUpdateSchedule: (mode: 'immediate' | 'schedule', sendAt?: string) => Promise<void>;
  onStopNextBatch: () => Promise<{ cancelled: number }>;
};

const THEMES = {
  meta: {
    border: 'border-amber-200 dark:border-amber-900/40',
    bg: 'bg-amber-50 dark:bg-amber-950/20',
    title: 'text-amber-900 dark:text-amber-200',
    sub: 'text-amber-800/80 dark:text-amber-300/80',
    btn: 'bg-amber-600 hover:bg-amber-700',
    btnOutline: 'border-amber-600 text-amber-800 dark:text-amber-200',
    accent: 'text-amber-700 dark:text-amber-300',
    tableHead: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  },
  rcs: {
    border: 'border-violet-200 dark:border-violet-900/40',
    bg: 'bg-violet-50 dark:bg-violet-950/20',
    title: 'text-violet-900 dark:text-violet-200',
    sub: 'text-violet-800/80 dark:text-violet-300/80',
    btn: 'bg-violet-600 hover:bg-violet-700',
    btnOutline: 'border-violet-500 text-violet-800 dark:text-violet-200',
    accent: 'text-violet-700 dark:text-violet-300',
    tableHead: 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300',
  },
};

const QUEUE_PAGE_SIZE = 40;
const STATUS_FILTERS = ['all', 'PENDING', 'SENT', 'FAILED', 'PROCESSING', 'CANCELLED'] as const;

const WhatsAppCampaignRetargetSection: React.FC<Props> = ({
  variant,
  campaignId,
  eligibleCount,
  stats,
  schedule,
  items,
  logs = [],
  analysis = null,
  attemptAnalysis = null,
  loading,
  showWhenEmpty = false,
  onRefresh,
  onEnqueue,
  onUpdateSchedule,
  onStopNextBatch,
}) => {
  const theme = THEMES[variant];
  const [showEnqueue, setShowEnqueue] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const [enqueueAt, setEnqueueAt] = useState('');
  const [rescheduleAt, setRescheduleAt] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [queueItems, setQueueItems] = useState<RetargetQueueItem[]>(items);
  const [queueTotal, setQueueTotal] = useState(items.length);
  const [queueLoading, setQueueLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('all');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [tryAnalysis, setTryAnalysis] = useState<MetaLimitAttemptAnalysis | null>(attemptAnalysis);
  const [rcsTemplateName, setRcsTemplateName] = useState('');
  const [rcsTemplates, setRcsTemplates] = useState<Array<{ name: string; displayName?: string }>>([]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(phoneSearch.trim()), 300);
    return () => window.clearTimeout(t);
  }, [phoneSearch]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, debouncedSearch, campaignId]);

  const loadQueuePage = useCallback(async () => {
    if (!campaignId) return;
    setQueueLoading(true);
    try {
      const params = {
        limit: QUEUE_PAGE_SIZE,
        offset: page * QUEUE_PAGE_SIZE,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: debouncedSearch || undefined,
      };
      const res =
        variant === 'meta'
          ? await fetchCampaignMetaLimitRetries(campaignId, params)
          : await fetchCampaignRcsRetries(campaignId, params);
      setQueueItems(res.data?.items || []);
      setQueueTotal(Number(res.data?.pagination?.total ?? res.data?.items?.length ?? 0));
      if (variant === 'meta' && res.data && 'attemptAnalysis' in res.data) {
        setTryAnalysis(res.data.attemptAnalysis || null);
      }
    } catch {
      /* parent refresh still has fallback items */
    } finally {
      setQueueLoading(false);
    }
  }, [campaignId, variant, page, statusFilter, debouncedSearch]);

  useEffect(() => {
    void loadQueuePage();
  }, [loadQueuePage]);

  useEffect(() => {
    if (variant !== 'rcs') return;
    fetchRcsStatus()
      .then((data) => {
        setRcsTemplates(data.templates || []);
        setRcsTemplateName((prev) => prev || data.templateName || data.templates?.[0]?.name || '');
      })
      .catch(() => setRcsTemplates([]));
  }, [variant]);

  useEffect(() => {
    if (!items.length || page !== 0 || statusFilter !== 'all' || debouncedSearch) return;
    setQueueItems(items);
  }, [items, page, statusFilter, debouncedSearch]);

  const title =
    variant === 'meta'
      ? 'Meta frequency cap — WA re-target'
      : 'Other failures — RCS re-target';

  const subtitle =
    variant === 'meta'
      ? 'Code 131049 · retry every 24h up to 7 days · matches Interakt Limited by Meta'
      : 'Non-Meta WhatsApp failures · requires INTERAKT_RCS_ENABLED';

  const hasPending = (schedule?.pending || stats?.PENDING || 0) > 0;
  const isScheduled = Boolean(schedule?.isScheduled && hasPending);
  const queueOverview = useMemo(() => {
    const pending = stats?.PENDING || 0;
    const sent = stats?.SENT || 0;
    const failed = stats?.FAILED || 0;
    const processing = stats?.PROCESSING || 0;
    const cancelled = stats?.CANCELLED || 0;
    const total = pending + sent + failed + processing + cancelled;
    return { total, pending, sent, failed, processing, cancelled };
  }, [stats]);

  const isEmpty =
    eligibleCount <= 0 &&
    !hasPending &&
    queueOverview.total <= 0 &&
    items.length === 0 &&
    logs.length === 0 &&
    !(analysis && analysis.attempted > 0);

  const pageCount = Math.max(1, Math.ceil(queueTotal / QUEUE_PAGE_SIZE));
  const showingFrom = queueTotal === 0 ? 0 : page * QUEUE_PAGE_SIZE + 1;
  const showingTo = Math.min(queueTotal, (page + 1) * QUEUE_PAGE_SIZE);

  React.useEffect(() => {
    if (schedule?.nextSendAt) {
      setRescheduleAt(toDatetimeLocalValue(schedule.nextSendAt));
    }
  }, [schedule?.nextSendAt]);

  const run = async (fn: () => Promise<void>) => {
    setActionLoading(true);
    try {
      await fn();
      await onRefresh();
      await loadQueuePage();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Action failed'));
    } finally {
      setActionLoading(false);
    }
  };

  if (isEmpty) {
    if (!showWhenEmpty) return null;
    if (loading) {
      return (
        <EmptyState
          title="Loading retarget data…"
          description="Fetching queue, schedule, and delivery logs."
          tone={variant === 'rcs' ? 'violet' : 'amber'}
        />
      );
    }
    return (
      <EmptyState
        title={
          variant === 'meta'
            ? 'No Meta-limited contacts for this campaign'
            : 'No other-failure contacts for RCS retarget'
        }
        description={
          variant === 'meta'
            ? 'When WhatsApp delivery fails with Meta frequency cap (131049 / Limited by Meta), those contacts appear here so you can queue WA retarget — immediate, scheduled, or 24h policy.'
            : 'When WhatsApp fails for non-Meta reasons (invalid number, template errors, etc.), those contacts appear here for RCS retarget. Enable INTERAKT_RCS_ENABLED and set a template to send.'
        }
        tone={variant === 'rcs' ? 'violet' : 'amber'}
      />
    );
  }

  return (
    <section className={`rounded-2xl border ${theme.border} ${theme.bg} p-5`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={`text-base font-semibold ${theme.title}`}>{title}</h3>
            {isScheduled && (
              <span
                className={`inline-flex items-center gap-1 rounded-full bg-white/80 px-2.5 py-0.5 text-xs font-semibold shadow-sm dark:bg-gray-900/80 ${
                  variant === 'rcs'
                    ? 'text-violet-800 dark:text-violet-200'
                    : 'text-amber-800 dark:text-amber-200'
                }`}
              >
                <ClockIcon className="h-3.5 w-3.5" />
                Scheduled · {formatDateTime(schedule?.nextSendAt)}
              </span>
            )}
            {hasPending && !isScheduled && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2.5 py-0.5 text-xs font-semibold text-green-800 shadow-sm dark:bg-gray-900/80 dark:text-green-300">
                <BoltIcon className="h-3.5 w-3.5" />
                Queued · sends soon
              </span>
            )}
            {!hasPending && queueOverview.cancelled > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2.5 py-0.5 text-xs font-semibold text-rose-800 shadow-sm dark:bg-gray-900/80 dark:text-rose-300">
                <StopCircleIcon className="h-3.5 w-3.5" />
                Next batch stopped
              </span>
            )}
          </div>
          <p className={`mt-1 text-xs ${theme.sub}`}>{subtitle}</p>
          <p className={`mt-2 text-xs ${theme.accent}`}>
            Eligible contacts: <strong>{eligibleCount || analysis?.attempted || 0}</strong>
            {stats
              ? ` · Queue: ${stats.PENDING || 0} pending · ${stats.SENT || 0} sent · ${stats.FAILED || 0} failed${
                  stats.CANCELLED ? ` · ${stats.CANCELLED} stopped` : ''
                }`
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={loading || actionLoading}
            onClick={() => void onRefresh()}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium dark:border-gray-600 dark:bg-gray-900"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
            {hasPending && (
              <button
                type="button"
                disabled={loading || actionLoading}
                onClick={() => setConfirmStop(true)}
                className="inline-flex items-center gap-1 rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60 dark:border-rose-800 dark:bg-gray-900 dark:text-rose-300 dark:hover:bg-rose-950/40"
              >
                <StopCircleIcon className="h-3.5 w-3.5" />
                Stop next batch
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowEnqueue((v) => !v)}
              className={`inline-flex items-center gap-1 rounded-lg ${theme.btn} px-3 py-1.5 text-xs font-medium text-white`}
            >
              <CalendarDaysIcon className="h-3.5 w-3.5" />
              New queue
            </button>
          </div>
      </div>

      {variant === 'meta' && analysis && analysis.attempted > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-amber-800/80 dark:text-amber-300/80">
            Limited by Meta · unique contacts
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
            <StatCard label="Attempted" value={analysis.attempted} hint="Ever Limited by Meta" />
            <StatCard
              label="Sent"
              value={analysis.sent}
              hint={`${analysis.attempted ? Math.round((analysis.sent / analysis.attempted) * 100) : 0}% of attempted`}
            />
            <StatCard label="Delivered" value={analysis.delivered} hint={`${analysis.deliveryRatePct}% of sent`} />
            <StatCard label="Read" value={analysis.read} hint={`${analysis.readRatePct}% of delivered`} />
            <StatCard label="Still limited" value={analysis.stillLimited} hint="Latest status still Meta-capped" />
            <StatCard label="Recovered" value={analysis.recovered} hint={`${analysis.recoveryRatePct}% later sent`} />
            <StatCard label="Failed now" value={analysis.failed} hint="Latest log still failed" />
          </div>
          <p className="mt-2 text-[11px] text-amber-800/70 dark:text-amber-300/70">
            Interakt-style: unique phones that hit Limited by Meta (131049), then their latest webhook status
            (includes retarget retries). Interakt has no public GET for campaign stats — numbers come from our
            delivery logs, same rules as Interakt.
          </p>
        </div>
      )}

      {variant === 'meta' && tryAnalysis?.attempts?.length ? (
        <div className="mt-4 rounded-xl border border-white/60 bg-white/80 p-4 dark:border-gray-700 dark:bg-gray-900/60">
          <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">7 retry attempts</p>
              <p className="text-[11px] text-amber-800/70 dark:text-amber-300/70">
                Queue {tryAnalysis.totalQueued} contacts · try 1 is first retarget · later tries only if still Limited by Meta
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="text-[10px] uppercase text-amber-800 dark:text-amber-300">
                <tr>
                  <th className="px-2 py-1.5 text-left">Try</th>
                  <th className="px-2 py-1.5 text-right">Pending</th>
                  <th className="px-2 py-1.5 text-right">Attempted</th>
                  <th className="px-2 py-1.5 text-right">Sent</th>
                  <th className="px-2 py-1.5 text-right">Delivered</th>
                  <th className="px-2 py-1.5 text-right">Read</th>
                  <th className="px-2 py-1.5 text-right">Failed</th>
                </tr>
              </thead>
              <tbody>
                {tryAnalysis.attempts.map((row) => (
                  <tr key={row.attempt} className="border-t border-amber-100/80 dark:border-amber-900/40">
                    <td className="px-2 py-1.5 font-semibold">{row.label}</td>
                    <td className="px-2 py-1.5 text-right">{row.pending}</td>
                    <td className="px-2 py-1.5 text-right">{row.attempted}</td>
                    <td className="px-2 py-1.5 text-right">{row.sent}</td>
                    <td className="px-2 py-1.5 text-right">
                      {row.delivered}
                      {row.sent ? <span className="ml-1 text-[10px] text-gray-400">{row.deliveryRatePct}%</span> : null}
                    </td>
                    <td className="px-2 py-1.5 text-right">{row.read}</td>
                    <td className="px-2 py-1.5 text-right">{row.failed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {hasPending && (
        <div className="mt-4 rounded-xl border border-white/60 bg-white/70 p-4 dark:border-gray-700 dark:bg-gray-900/60">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Active queue schedule</p>
          <p className="mt-1 text-xs text-gray-500">
            {isScheduled
              ? `Next send: ${formatDateTime(schedule?.nextSendAt)} (${schedule?.pending || stats?.PENDING} contacts)`
              : `${schedule?.pending || stats?.PENDING} contacts queued for immediate processing`}
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <button
              type="button"
              disabled={actionLoading}
              onClick={() =>
                void run(async () => {
                  await onUpdateSchedule('immediate');
                  toast.success('Changed to immediate send');
                })
              }
              className={`inline-flex items-center gap-1 rounded-lg ${theme.btn} px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60`}
            >
              <BoltIcon className="h-3.5 w-3.5" />
              Send now
            </button>
            <label className="text-xs text-gray-600 dark:text-gray-300">
              Reschedule to
              <input
                type="datetime-local"
                value={rescheduleAt}
                onChange={(e) => setRescheduleAt(e.target.value)}
                className={`${inputClass} mt-1 block text-xs`}
              />
            </label>
            <button
              type="button"
              disabled={actionLoading || !rescheduleAt}
              onClick={() =>
                void run(async () => {
                  await onUpdateSchedule('schedule', new Date(rescheduleAt).toISOString());
                  toast.success('Schedule updated');
                })
              }
              className={`rounded-lg border ${theme.btnOutline} px-3 py-1.5 text-xs font-medium disabled:opacity-60`}
            >
              Update schedule
            </button>
            <button
              type="button"
              disabled={actionLoading}
              onClick={() => setConfirmStop(true)}
              className="inline-flex items-center gap-1 rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60 dark:border-rose-800 dark:bg-gray-900 dark:text-rose-300"
            >
              <StopCircleIcon className="h-3.5 w-3.5" />
              Stop next batch
            </button>
          </div>
        </div>
      )}

      {showEnqueue && (
        <div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-white/80 p-4 dark:border-gray-600 dark:bg-gray-900/50">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Queue new retarget batch</p>
          {variant === 'rcs' && (
            <div className="mt-3 space-y-2">
              {rcsTemplates.length > 0 && (
                <select
                  value={rcsTemplateName}
                  onChange={(e) => setRcsTemplateName(e.target.value)}
                  className={`${inputClass} text-xs`}
                >
                  <option value="">Select RCS template…</option>
                  {rcsTemplates.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.displayName || t.name}
                    </option>
                  ))}
                </select>
              )}
              <input
                value={rcsTemplateName}
                onChange={(e) => setRcsTemplateName(e.target.value)}
                placeholder="RCS template code name"
                className={`${inputClass} text-xs`}
              />
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={actionLoading}
              onClick={() =>
                void run(async () => {
                  await onEnqueue('immediate', undefined, { rcsTemplateName: rcsTemplateName || undefined });
                  toast.success('Queued for immediate send');
                  setShowEnqueue(false);
                })
              }
              className={`rounded-lg ${theme.btn} px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60`}
            >
              Immediate
            </button>
            {variant === 'meta' && (
              <button
                type="button"
                disabled={actionLoading}
                onClick={() =>
                  void run(async () => {
                    await onEnqueue('policy24h');
                    toast.success('Queued — first send in 24h, then every 24h if still limited');
                    setShowEnqueue(false);
                  })
                }
                className={`rounded-lg border ${theme.btnOutline} px-3 py-1.5 text-xs font-medium disabled:opacity-60`}
              >
                First send in 24h
              </button>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="text-xs text-gray-600 dark:text-gray-300">
              Or schedule for
              <input
                type="datetime-local"
                value={enqueueAt}
                onChange={(e) => setEnqueueAt(e.target.value)}
                className={`${inputClass} mt-1 block text-xs`}
              />
            </label>
            <button
              type="button"
              disabled={actionLoading || !enqueueAt}
              onClick={() =>
                void run(async () => {
                  await onEnqueue('schedule', new Date(enqueueAt).toISOString(), {
                    rcsTemplateName: rcsTemplateName || undefined,
                  });
                  toast.success('Scheduled retarget queued');
                  setShowEnqueue(false);
                })
              }
              className={`rounded-lg ${theme.btn} px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60`}
            >
              Queue scheduled
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowDetails((v) => !v)}
        className="mt-4 flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white/60 px-3 py-2 text-left text-xs font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200"
      >
        <span className="inline-flex items-center gap-1.5">
          <DocumentTextIcon className="h-4 w-4" />
          Queue contacts ({queueOverview.total || queueTotal} total
          {logs.length ? ` · ${logs.length} delivery` : ''})
        </span>
        {showDetails ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
      </button>

      {showDetails && (
        <div className="mt-3 space-y-4">
          <div className="flex flex-wrap gap-2">
            {(
              [
                ['all', 'All', queueOverview.total],
                ['PENDING', 'Pending', queueOverview.pending],
                ['SENT', 'Sent', queueOverview.sent],
                ['FAILED', 'Failed', queueOverview.failed],
                ['PROCESSING', 'Processing', queueOverview.processing],
                ['CANCELLED', 'Stopped', queueOverview.cancelled],
              ] as const
            ).map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  statusFilter === key
                    ? `${theme.btn} text-white`
                    : 'border border-gray-200 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200'
                }`}
              >
                {label} <span className="opacity-80">{count}</span>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <input
              value={phoneSearch}
              onChange={(e) => setPhoneSearch(e.target.value)}
              placeholder="Search phone…"
              className={`${inputClass} max-w-xs text-xs`}
            />
            <p className="text-[11px] text-gray-500">
              {queueLoading
                ? 'Loading…'
                : queueTotal === 0
                  ? 'No contacts match'
                  : `Showing ${showingFrom}–${showingTo} of ${queueTotal}`}
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
            <table className="min-w-full text-xs">
              <thead className={`text-[10px] uppercase ${theme.tableHead}`}>
                <tr>
                  <th className="px-3 py-2 text-left">Phone</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Send at</th>
                  <th className="px-3 py-2 text-left">Attempts</th>
                  <th className="px-3 py-2 text-left">Last error</th>
                </tr>
              </thead>
              <tbody>
                {queueItems.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                      {queueLoading ? 'Loading queue…' : 'No queue contacts for this filter'}
                    </td>
                  </tr>
                ) : (
                  queueItems.map((item) => (
                    <tr key={item.id} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-3 py-2 font-mono">{item.phone}</td>
                      <td className="px-3 py-2">
                        <StatusBadge value={item.status} />
                      </td>
                      <td className="px-3 py-2">{formatDateTime(item.sendAt)}</td>
                      <td className="px-3 py-2">
                        {item.attemptCount ?? 0}/{item.maxAttempts ?? '—'}
                      </td>
                      <td className="max-w-[180px] truncate px-3 py-2" title={item.lastError || ''}>
                        {item.lastError || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                disabled={page <= 0 || queueLoading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900"
              >
                <ChevronLeftIcon className="h-3.5 w-3.5" />
                Previous
              </button>
              <p className="text-xs text-gray-500">
                Page {page + 1} of {pageCount}
              </p>
              <button
                type="button"
                disabled={page + 1 >= pageCount || queueLoading}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900"
              >
                Next
                <ChevronRightIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {variant === 'rcs' && logs.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">RCS delivery logs</p>
              <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                <table className="min-w-full text-xs">
                  <thead className={`text-[10px] uppercase ${theme.tableHead}`}>
                    <tr>
                      <th className="px-3 py-2 text-left">Phone</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Template</th>
                      <th className="px-3 py-2 text-left">Error</th>
                      <th className="px-3 py-2 text-left">Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.slice(0, 50).map((log) => (
                      <tr key={log.id} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="px-3 py-2 font-mono">{log.phone}</td>
                        <td className="px-3 py-2">
                          <StatusBadge value={log.status} />
                        </td>
                        <td className="px-3 py-2">{log.rcsTemplateName || '—'}</td>
                        <td className="max-w-[180px] truncate px-3 py-2" title={log.errorMessage || ''}>
                          {log.errorMessage || '—'}
                        </td>
                        <td className="px-3 py-2">{formatDateTime(log.sentAt || log.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {variant === 'meta' &&
            queueItems.some((i) => (i.meta?.attemptsLog || []).length > 0) && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Attempt history</p>
                <div className="max-h-48 overflow-auto rounded-lg border border-gray-200 bg-white p-3 text-xs dark:border-gray-700 dark:bg-gray-900">
                  {queueItems
                    .filter((i) => (i.meta?.attemptsLog || []).length)
                    .slice(0, 20)
                    .map((item) => (
                      <div key={item.id} className="mb-2 border-b border-gray-100 pb-2 last:border-0 dark:border-gray-800">
                        <p className="font-mono font-medium">{item.phone}</p>
                        {(item.meta?.attemptsLog || []).map((a) => (
                          <p key={`${item.id}-${a.attempt}`} className="text-gray-600 dark:text-gray-400">
                            #{a.attempt} · {formatDateTime(a.at)} · {a.sent ? 'sent' : `failed: ${a.reason || '—'}`}
                          </p>
                        ))}
                      </div>
                    ))}
                </div>
              </div>
            )}
        </div>
      )}

      <ConfirmDialog
        open={confirmStop}
        title="Stop next retarget batch?"
        tone="danger"
        confirmLabel={actionLoading ? 'Stopping…' : 'Stop batch'}
        cancelLabel="Keep scheduled"
        busy={actionLoading}
        message={
          <>
            {schedule?.pending || stats?.PENDING || 0} pending contacts will not be sent
            {isScheduled && schedule?.nextSendAt ? ` at ${formatDateTime(schedule.nextSendAt)}` : ''}.
            Already sent or in-progress rows stay as they are. You can queue a new batch later.
          </>
        }
        onCancel={() => setConfirmStop(false)}
        onConfirm={() =>
          void run(async () => {
            const result = await onStopNextBatch();
            setConfirmStop(false);
            toast.success(
              result.cancelled > 0
                ? `Stopped next batch — ${result.cancelled} contacts cancelled`
                : 'No pending contacts to stop'
            );
          })
        }
      />
    </section>
  );
};

export default WhatsAppCampaignRetargetSection;
